import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OperatorModel, OperatorService } from '@railmybox/api-dispo';
import { map, Observable, of, switchMap } from 'rxjs';
import { AbstractValueAccessorComponent, ValueAccessorUtil } from '@railmybox/shared/util';

@UntilDestroy()
@Component({
  selector: 'rmb-operator-select',
  templateUrl: './operator-select.component.html',
  styleUrls: ['./operator-select.component.scss'],
  providers: [...ValueAccessorUtil.getValueAccessorProvider(OperatorSelectComponent)],
  standalone: false,
})
export class OperatorSelectComponent extends AbstractValueAccessorComponent<string | OperatorModel> implements OnInit {
  private operatorService = inject(OperatorService);

  operators$: Observable<OperatorModel[]>;
  searchControl: FormControl = new FormControl<string | null>(undefined);
  selectedName: string;
  @Input() filter: string;
  @Input() selected: string;
  @Input() clearable = true;
  @Input() type: 'id' | 'operator' = 'id';

  ngOnInit(): void {
    this.operators$ = this.searchControl.valueChanges.pipe(
      untilDestroyed(this),
      switchMap((query: string) => (query ? this.operatorService.adminListOperators({ query }) : of([]))),
      map((operators: OperatorModel[]) =>
        this.filter ? operators.filter((model) => `${model.id} ${model.name}`.toLowerCase().includes(this.filter.toLowerCase())) : operators
      )
    );
  }

  onSearchValueChanged(query: string): void {
    this.searchControl.patchValue(query);
  }

  onSelect(operator: OperatorModel): void {
    this.selectedName = operator?.name;
    this.value = this.type == 'id' ? operator?.id : operator;
    this.onChange(this.value);
    this.changed.emit(this.value);
  }

  async writeValue(value: string | OperatorModel): Promise<void> {
    super.writeValue(value);

    if (!value) {
      this.selectedName = null;
      return;
    }
    const operator = await this.operatorService
      .adminListOperators()
      .toPromise()
      .then((filteredValues) => filteredValues.find((operator) => operator.id === value));

    // In case no entry found with that id
    if (!operator) {
      return;
    }

    this.value = operator.id;
    this.selectedName = operator.name;
  }
}
